import { zodResolver } from "@hookform/resolvers/zod";
import { useGoogleLogin } from "@react-oauth/google";
import { signInWithCustomToken } from "firebase/auth";
import { Lock, Mail, MapPin, Phone, School, User } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import z from "zod";
import { auth } from "../../firebaseConfig";
import { endpoints } from "../../hooks/endpoints";
import { useAppDispatch } from "../../store/hooks";
import { userData } from "../../store/slices/storeSlice";
import Spinner from "../Spinner";
import { Button } from "../ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";

export const phoneSchema = z
  .string()
  .refine((val) => /^\d+$/.test(val), {
    message: "يجب ان يحتوي رقم الهاتف علي ارقام فقط",
  })
  .refine((val) => /^01/.test(val), {
    message: "يجب ان يبدأ رقم الهاتف بـ 01 باللغة الإنجليزية",
  })
  .refine((val) => /^\d{11}$/.test(val), {
    message: "يجب ان يحتوي رقم الهاتف علي 11 رقم فقط",
  });

const signupFormSchema = z.object({
  fullName: z
    .string()
    .min(4, {
      message: "يجب أن يكون الإسم اكثر من ٤ حروف",
    })
    .max(50, {
      message: "يجب أن يكون الإسم اقل من ٥٠ حرف",
    })
    .refine((val) => val.split(" ").length > 3, {
      message: "يجب أن يكون الإسم رباعي بالعربي",
    }),
  email: z.string().email({ message: "هذا البريد غير صحيح" }),
  phone: phoneSchema,
  fatherPhone: phoneSchema,
  motherPhone: phoneSchema,
  city: z.string().min(3, {
    message: "يجب إدخال المحافظة",
  }),
  school: z.string().min(4, {
    message: "يجب إدخال المدرسة",
  }),
  password: z
    .string()
    .min(3, {
      message: "يجب إدخال كلمة مرور",
    })
    .max(50),
  terms: z.boolean().refine((val) => val, {
    message: "يجب قبول شروط الاستخدام و سياسة الخصوصية",
  }),
});

interface RegistrationFormI {
  registerPhone: string;
  registerName: string;
  registerEmail: string;
  registerPassword: string;
  registerSchool: string;
  registerCity: string;
  registerFatherPhone: string;
  registerMotherPhone: string;
}

const RegistrationForm = () => {
  const username2 = useRef<HTMLInputElement>();
  const email = useRef<HTMLInputElement>();
  const school = useRef<HTMLInputElement>();
  const city = useRef<HTMLInputElement>();
  const firstName = useRef<HTMLInputElement>();
  const fatherPhone = useRef<HTMLInputElement>();
  const motherPhone = useRef<HTMLInputElement>();
  const password2 = useRef<HTMLInputElement>();

  const [isLoginPending, setIsLoginPending] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [arabicAlert, setArabicAlert] = useState(false);
  const [isGoogleWindowOpen, setIsGoogleWindowOpen] = useState(false);
  const [isLoginWithFacebook, setIsLoginWithFacebook] = useState(false);

  const form = useForm<z.infer<typeof signupFormSchema>>({
    resolver: zodResolver(signupFormSchema),
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      fatherPhone: "",
      motherPhone: "",
      city: "",
      school: "",
      password: "",
      terms: false,
    },
  });

  const removeArabicChars = (e) => {
    let str = e.target.value;
    const newStr = str.replaceAll(/[\u0600-\u06FF]/g, "");
    e.target.value = newStr;
    setArabicAlert(str !== newStr);
  };

  useEffect(() => {
    if (!arabicAlert) return;
    setTimeout(() => {
      setArabicAlert(false);
    }, 5000);
  }, [arabicAlert]);

  const handleRegistrationSubmit = () => {
    setIsLoginPending(true);
    const credentials = {
      username: form.getValues().phone,
      password: form.getValues().password,
      email: form.getValues().email,
      father_phone: form.getValues().fatherPhone,
      mother_phone: form.getValues().motherPhone,
      school: form.getValues().school,
      city: form.getValues().city,
      first_name: form.getValues().fullName,
    };
    fetch(endpoints.createUser, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(credentials),
    })
      .then((res) => res.json())
      .then((result) => {
        console.log("result", result);
        if (result.status === 400) {
          toast.error("حدث خطأ", { description: result.message });
        } else if (result.token) {
          signInWithCustomToken(auth, result.firebase_token)
            .then((userCredential) => {
              const user = userCredential.user;
              dispatch(userData(result));
              localStorage.setItem("user", JSON.stringify(result));
              console.log("User Logged In");
              navigate("/");
            })
            .catch((error) => {
              const errorCode = error.code;
              const errorMessage = error.message;
              throw new Error(errorMessage);
            });
        } else if (!result.is_valid) {
          form.clearErrors();

          if (result.field === "phone") {
            form.setError("phone", { type: "manual", message: result.message });
          } else if (result.field === "father_phone") {
            form.setError("fatherPhone", {
              type: "manual",
              message: result.message,
            });
          } else if (result.field === "mother_phone") {
            form.setError("motherPhone", {
              type: "manual",
              message: result.message,
            });
          } else if (result.field === "name") {
            form.setError("fullName", {
              type: "manual",
              message: result.message,
            });
          } else if (result.field === "email") {
            form.setError("email", { type: "manual", message: result.message });
          }
        }
      })
      .catch((err) => {
        console.log(err.message);
        toast.error("حدث خطأ");
      })
      .finally(() => {
        setIsGoogleWindowOpen(false);
        setIsLoginPending(false);
      });
  };

  const validateGoogleTokenOnServer = (code, currentPageUrl) => {
    setIsLoginPending(true);
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      code,
      url: currentPageUrl,
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    } as RequestInit;
    fetch(endpoints.googleSocialLogin, requestOptions)
      .then((res) => res.json())
      .then((result) => {
        console.log(result, "credentialResponse");
        if (result.token) {
          signInWithCustomToken(auth, result.firebase_token)
            .then((userCredential) => {
              setIsLoginPending(false);
              const user = userCredential.user;
              console.log({ user });
              dispatch(userData(result));
              localStorage.setItem("user", JSON.stringify(result));
              setIsGoogleWindowOpen(false);
              navigate("/");
            })
            .catch((error) => {
              setIsLoginPending(false);
              const errorCode = error.code;
              const errorMessage = error.message;
              throw new Error(errorMessage);
            });
        } else {
          setIsLoginPending(false);
          toast.error("الإيميل مش صح");
          setIsGoogleWindowOpen(false);
        }
      })
      .catch((err) => {
        console.log(err.message);
        toast.error("حدث خطأ");
        setIsGoogleWindowOpen(false);
      });
  };

  const loginWithGoogle = useGoogleLogin({
    flow: "auth-code",
    onSuccess: (GoogleResponse) => {
      console.log("Login Success:", GoogleResponse);
      validateGoogleTokenOnServer(GoogleResponse.code, window.location.origin);
    },
    onError: (error) => {
      console.log("Login Failed:", error);
      toast.error("حدث خطأ");
      setIsGoogleWindowOpen(false);
    },
    onNonOAuthError: () => {
      setIsGoogleWindowOpen(false);
    },
  });

  return (
    <>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleRegistrationSubmit)}
          className="space-y-4"
        >
          <div className="flex flex-col lg:flex-row gap-2">
            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem className="grow md:order-2">
                  <FormLabel className="flex items-center rtl:justify-end">
                    الاسم رباعي بالعربي
                    <User className="w-4 h-4 mr-2" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      className="rtl:text-right"
                      placeholder="مثال: محمد احمد على محمود"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="rtl:text-right" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="grow md:order-1">
                  <FormLabel className="flex items-center rtl:justify-end">
                    الإيميل
                    <Mail className="w-4 h-4 mr-2" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      className="rtl:text-right"
                      placeholder="mohamed@gmail.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="rtl:text-right" />
                </FormItem>
              )}
            />
          </div>
          <div className="flex flex-col lg:flex-row gap-2">
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem className="grow md:order-2">
                  <FormLabel className="flex items-center rtl:justify-end">
                    رقم الهاتف
                    <Phone className="w-4 h-4 mr-2" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="tel"
                      className="rtl:text-right"
                      placeholder="0100 123 4567"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="rtl:text-right" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="fatherPhone"
              render={({ field }) => (
                <FormItem className="grow md:order-1">
                  <FormLabel className="flex items-center rtl:justify-end">
                    هاتف الأب
                    <Phone className="w-4 h-4 mr-2" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="tel"
                      className="rtl:text-right"
                      placeholder="0100 123 4567"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="rtl:text-right" />
                </FormItem>
              )}
            />
          </div>
          <div className="flex flex-col lg:flex-row gap-2">
            <FormField
              control={form.control}
              name="motherPhone"
              render={({ field }) => (
                <FormItem className="grow md:order-2">
                  <FormLabel className="flex items-center rtl:justify-end">
                    هاتف الأم
                    <Phone className="w-4 h-4 mr-2" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="tel"
                      className="rtl:text-right"
                      placeholder="0100 123 4567"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="rtl:text-right" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem className="grow md:order-1">
                  <FormLabel className="flex items-center rtl:justify-end">
                    المحافظة
                    <MapPin className="w-4 h-4 mr-2" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      className="rtl:text-right"
                      placeholder="القاهرة"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="rtl:text-right" />
                </FormItem>
              )}
            />
          </div>
          <div className="flex flex-col lg:flex-row gap-2">
            <FormField
              control={form.control}
              name="school"
              render={({ field }) => (
                <FormItem className="grow md:order-2">
                  <FormLabel className="flex items-center rtl:justify-end">
                    المدرسة
                    <School className="w-4 h-4 mr-2" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      className="rtl:text-right"
                      placeholder="المدرسة الثانوية"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="rtl:text-right" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="grow md:order-1">
                  <FormLabel className="flex items-center rtl:justify-end">
                    كلمة المرور
                    <Lock className="w-4 h-4 mr-2" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      className="rtl:text-right"
                      placeholder="******"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="rtl:text-right" />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="terms"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-2 my-5">
                <div className="flex gap-2 rtl:justify-end">
                  <FormLabel className="flex items-center rtl:flex-row-reverse">
                    أوافق على{" "}
                    <Link
                      to="/privacy-policy"
                      className="p-0 h-auto text-sm underline"
                    >
                      شروط الاستخدام و سياسة الخصوصية
                    </Link>
                  </FormLabel>
                  <FormControl>
                    <input
                      type="checkbox"
                      id="terms"
                      className="rounded"
                      checked={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  </FormControl>
                </div>
                <FormMessage className="rtl:text-right" />
              </FormItem>
            )}
          />

          <div className="flex flex-col md:flex-row gap-3">
            <Button
              className="grow md:max-w-1/2 md:order-2"
              size="lg"
              disabled={isLoginPending}
            >
              {isLoginPending ? <Spinner variant="button" /> : "إنشاء حساب"}
            </Button>
            <Button
              type="button"
              size="lg"
              className="bg-[#ef523e] hover:bg-[#ef523e]/90 grow md:max-w-1/2 md:order-1"
              disabled={isGoogleWindowOpen}
              onClick={() => {
                loginWithGoogle();
                setIsGoogleWindowOpen(true);
              }}
            >
              <img
                style={{ margin: "0 5px", justifySelf: "center" }}
                src="https://eliteacademymedia.s3.us-east-1.amazonaws.com/qashqool_assets/media/google-icon.svg"
                alt="google"
              />{" "}
              إنشاء حساب بجوجل
            </Button>
          </div>
        </form>
      </Form>
      {/* <Separator />
      <div className="flex justify-center items-center">
        <Button
          type="button"
          className="google-login text-white social-login-btns w-auto"
          disabled={isGoogleWindowOpen}
          style={{
            pointerEvents: isGoogleWindowOpen ? "none" : "auto",
            opacity: isGoogleWindowOpen ? "0.5" : "1",
          }}
          onClick={() => {
            loginWithGoogle();
            setIsGoogleWindowOpen(true);
          }}
        >
          <img
            style={{ margin: "0 5px", justifySelf: "center" }}
            src="https://eliteacademymedia.s3.us-east-1.amazonaws.com/qashqool_assets/media/google-icon.svg"
            alt="google"
          />{" "}
          إنشاء حساب بجوجل
        </Button>
      </div> */}
    </>
  );
};

export default RegistrationForm;
